import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * React Router does NOT reset scroll position on navigation — it is a
 * client-side route change, not a page load. Without this, clicking a nav
 * link from halfway down a long page lands you halfway down the next one,
 * which is exactly the "doesn't scroll properly" feeling.
 *
 * Also handles #anchor links: those scroll smoothly to the section instead
 * of jumping to the top.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    if (hash) {
      // By id, not querySelector: "#1", "#_=_" or "#cennik?utm=…" are not
      // valid selectors, and the SyntaxError blanked the whole app.
      let el: HTMLElement | null = null
      try {
        el = document.getElementById(decodeURIComponent(hash.slice(1)))
      } catch {
        el = null
      }
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
    }
    // 'instant' on purpose: smooth-scrolling the whole page on every route
    // change feels sluggish and slightly seasick. An engine that does not know
    // 'instant' throws on the options object; the plain form always works.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    } catch {
      window.scrollTo(0, 0)
    }
  }, [pathname, hash])

  return null
}
